import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import ReactQuill from "react-quill-new";
import "quill/dist/quill.snow.css";
import { useGlobalAIDrawers } from "../hooks/useGlobalAIDrawers";

export interface RichTextEditorProps {
  value: string;
  placeholder?: string;
  onChange: (val: string) => void;
  onBlur: () => void;
  minHeight?: number;
  showControls?: boolean;
  className?: string;
  disabled?: boolean;
  enableAITextGeneration?: boolean;
  aiPromptContext?: string;
  autoConvertWhiteToBlack?: boolean;
}

export interface RichTextEditorRef {
  focus: () => void;
  getQuill: () => ReactQuill["getEditor"] extends () => infer T ? T | null : never;
  insertText: (text: string) => void;
}

/**
 * Utility function to convert ONLY white colors to black for better visibility in editor
 * This ensures white text appears as black in the editor while preserving original colors in HTML output
 * Extracted outside component for reusability and testability
 */
const convertWhiteColorsToBlack = (htmlContent: string): string => {
  if (!htmlContent) return htmlContent;

  // Comprehensive patterns to match ALL white color formats
  const whiteColorPatterns = [
    // Hex colors: #fff, #ffffff (case insensitive, with word boundaries)
    /color:\s*#fff(?![0-9a-fA-F])/gi,
    /color:\s*#ffffff(?![0-9a-fA-F])/gi,
    
    // Named color: white (case insensitive, with word boundaries)
    /color:\s*white\b/gi,
    
    // RGB: rgb(255, 255, 255) with optional spaces
    /color:\s*rgb\(\s*255\s*,\s*255\s*,\s*255\s*\)/gi,
    
    // RGBA: rgba(255, 255, 255, any_alpha) with optional spaces
    /color:\s*rgba\(\s*255\s*,\s*255\s*,\s*255\s*,\s*[0-9]*\.?[0-9]+\s*\)/gi,
    
    // HSL: hsl(0, 0%, 100%) - pure white in HSL
    /color:\s*hsl\(\s*0\s*,\s*0%\s*,\s*100%\s*\)/gi,
    
    // HSLA: hsla(0, 0%, 100%, any_alpha) - pure white in HSLA
    /color:\s*hsla\(\s*0\s*,\s*0%\s*,\s*100%\s*,\s*[0-9]*\.?[0-9]+\s*\)/gi,
    
    // Additional white variations
    /color:\s*#FFFFFF(?![0-9a-fA-F])/gi, // uppercase
    /color:\s*#FFF(?![0-9a-fA-F])/gi,     // uppercase
    /color:\s*WHITE\b/gi,                  // uppercase
  ];

  return whiteColorPatterns.reduce(
    (acc, pattern) => acc.replace(pattern, "color: #000000"),
    htmlContent
  );
};

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    {
      value,
      placeholder = "Enter text...",
      onChange,
      onBlur,
      minHeight = 120,
      showControls = true,
      className = "",
      disabled = false,
      enableAITextGeneration = true,
      aiPromptContext = "",
      autoConvertWhiteToBlack = false,
    },
    ref
  ) => {
    const [draft, setDraft] = useState<string>(value);
    const [customColor, setCustomColor] = useState<string>("#000000");
    const quillRef = useRef<ReactQuill>(null);
    const { openTextDrawer } = useGlobalAIDrawers();

    // Sync external value + auto color conversion
    useEffect(() => {
      setDraft(autoConvertWhiteToBlack ? convertWhiteColorsToBlack(value) : value);
    }, [value, autoConvertWhiteToBlack]);

    useImperativeHandle(ref, () => ({
      focus: () => quillRef.current?.focus(),
      getQuill: () => quillRef.current?.getEditor() || null,
      insertText: (text: string) => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        quill.focus();
        let range = quill.getSelection();
        if (!range) {
          range = { index: quill.getLength() - 1, length: 0 };
        }
        quill.insertText(range.index, text);
        quill.setSelection(range.index + text.length);

        const newContent = quill.root.innerHTML;
        setDraft(newContent);
        onChange(newContent);
      },
    }), [onChange]);

    const imageHandler = useCallback(() => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.click();
      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
          const quill = quillRef.current?.getEditor();
          const range = quill?.getSelection();
          if (quill && range) {
            quill.insertEmbed(range.index, "image", reader.result as string);
            quill.setSelection(range.index + 1);
          }
        };
        reader.readAsDataURL(file);
      };
    }, []);

    const applyCustomColor = useCallback((color: string) => {
      const quill = quillRef.current?.getEditor();
      const range = quill?.getSelection();
      if (quill && range) {
        quill.format("color", color);
      }
    }, []);

    const handleOpenAIDrawer = useCallback(() => {
      openTextDrawer(aiPromptContext, value, (generatedText: string) => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        quill.focus();
        let range = quill.getSelection();
        if (!range) {
          range = { index: quill.getLength() - 1, length: 0 };
        }
        quill.insertText(range.index, generatedText);
        quill.setSelection(range.index + generatedText.length);

        const newContent = quill.root.innerHTML;
        setDraft(newContent);
        onChange(newContent);
      });
    }, [aiPromptContext, onChange, openTextDrawer, value]);

    const modules = useMemo(() => ({
      toolbar: {
        container: [
          ["bold", "italic", "underline", "strike"],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
          ["customColor"],
        ],
        handlers: {
          image: imageHandler,
          customColor: () => document.getElementById("hidden-color-input")?.click(),
        },
      },
    }), [imageHandler]);

    const formats = useMemo(() => [
      "header", "bold", "italic", "underline", "strike",
      "color", "background", "align",
      "link", "image",
    ], []);

    const handleSave = useCallback(() => {
      // Get the current HTML from Quill editor
      const quill = quillRef.current?.getEditor();
      let currentHtml = quill ? quill.root.innerHTML : draft;
      
      // If autoConvertWhiteToBlack is enabled, we need to get the original content
      // and apply conversion, since the draft might already be converted for display
      if (autoConvertWhiteToBlack) {
        // For footer editors, we want to convert white colors to black in the final output
        currentHtml = convertWhiteColorsToBlack(currentHtml);
      }
      
      onChange(currentHtml);
      onBlur();
    }, [draft, onChange, onBlur, autoConvertWhiteToBlack]);

    const handleCancel = useCallback(() => {
      setDraft(value);
      onBlur();
    }, [value, onBlur]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === "Escape") handleCancel();
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSave();
    }, [handleCancel, handleSave]);

    return (
      <div className={`rich-text-editor-container max-w-fit mx-auto ${className} ${disabled ? "cursor-not-allowed" : ""}`}>
        {enableAITextGeneration && (
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={handleOpenAIDrawer}
              disabled={disabled}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50"
              aria-label="Generate text using AI"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Generate with AI
            </button>
          </div>
        )}

        <ReactQuill
          ref={quillRef}
          value={draft}
          onChange={(val) => {
            if (disabled) return;
            // Apply conversion for display only when prop is enabled
            setDraft(autoConvertWhiteToBlack ? convertWhiteColorsToBlack(val) : val);
            // Apply conversion if enabled, otherwise pass original
            if (!showControls) {
              const finalValue = autoConvertWhiteToBlack ? convertWhiteColorsToBlack(val) : val;
              onChange(finalValue);
            }
          }}
          theme="snow"
          placeholder={placeholder}
          modules={disabled ? { toolbar: false } : modules}
          formats={formats}
          onKeyDown={disabled ? undefined : handleKeyDown}
          readOnly={disabled}
          style={{
            backgroundColor: disabled ? "#f9fafb" : "#ffffff",
            minHeight,
            borderRadius: showControls ? "8px 8px 0 0" : "8px",
            border: `1px solid ${disabled ? "#e5e7eb" : "#d1d5db"}`,
            pointerEvents: disabled ? "none" : "auto",
          }}
        />

        <input
          id="hidden-color-input"
          type="color"
          value={customColor}
          onChange={(e) => {
            setCustomColor(e.target.value);
            applyCustomColor(e.target.value);
          }}
          className="hidden"
          aria-label="Custom text color selector"
        />

        {showControls && (
          <>
            <div className="flex justify-end gap-2 p-3 bg-gray-50 border border-t-0 border-gray-300 rounded-b-lg">
              <button
                onClick={handleCancel}
                className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
              >
                Save
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Press <kbd className="bg-gray-200 rounded px-1">Esc</kbd> to cancel,{" "}
              <kbd className="bg-gray-200 rounded px-1">Ctrl+Enter</kbd> to save
            </div>
          </>
        )}
      </div>
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";
export default RichTextEditor;
